import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogActions, DialogTitle, TextField, InputAdornment, Button, IconButton, Alert, AlertTitle, Box, FormHelperText, Paper, FormControl, Select, InputLabel, MenuItem, Grid } from "@mui/material";
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close';
/* REQUEST */
import { updateReport } from "utils/Reports";

const PaperComponent = (props) => {
    const nodeRef = useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const EditReports = ({ open, setOpen, rows, selected, setAlert, setOpenAlert, reloading }) => {
    const {t} = useTranslation()
    const [name, setName] = useState('')
    const [nameLength, setNameLength] = useState(0)
    const [status, setStatus] = useState('')
    const [for_employee, setForEmployee] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionLength, setDescriptionLength] = useState(0)
    const [slug, setSlug] = useState('')
    const [slugLength, setSlugLength] = useState(0)
    const [category, setCategory] = useState('')
    const [alerts, setAlerts] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (open && selected) {
            const row = rows.find((row) => row.id === selected[0])
            setName(row.name)
            setStatus(row.status)
            setForEmployee(row.for_employee)
            setDescription(row.description)
            setSlug(row.slug)
            setCategory(row.category)
        }
    }, [selected, rows, open ])

    const existName = (name) => {
        let exist = false
        rows.forEach((row) => {
            if (String(row.name).toLocaleLowerCase() === String(name).toLocaleLowerCase()) {
                exist = true
            }
        })
        return exist
    }

    const closeForm = () => {
        setOpen(false)
        setName('')
        setStatus('')
        setForEmployee('')
        setDescription('')
        setSlug('')
        setCategory('')
    }

    const changes = () => {
        const hasChanges = name !== '' || description !== '' || status !== '' || for_employee !== '' || category !== '' || slug !== ''
        return hasChanges ? setAlerts(true) : closeForm()
    }

    const disabled = () => {
        return name === '' || description === '' || status === '' || for_employee === '' || category === '' || slug === ''
    }

    const editReport = async () => {
        setLoading(true)
        try {
            const response = await updateReport({
                report_id: selected[0],
                name: name,
                status: status,
                for_employees: for_employee,
                description: description,
                slug: slug,
                category_id: category
            })
            if(response.length > 0) {
                setOpenAlert(true)
                setAlert({
                    severity: 'success',
                    msg: t('custom_reports.reports.table.dialog.edit.messages.success')
                })
                setLoading(false)
                reloading()
                closeForm()
            } else {
                setAlert({
                    severity: 'error',
                    msg: t('custom_reports.reports.table.dialog.edit.messages.error')
                })
                setOpenAlert(true)
                setLoading(false)
            }
        } catch (error) {
            console.error(error)
            setAlert({
                msg: t('custom_reports.reports.table.dialog.edit.messages.error'),
                severity: 'error'
            })
            setOpenAlert(true)
            setLoading(false)
        }
    }
    return (
        <Dialog
            maxWidth="xs"
            fullWidth
            open={open}
            PaperComponent={PaperComponent}
            onClose={(event, reason) =>{
                if(reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">{t('custom_reports.reports.table.dialog.edit.title')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top : 0, zIndex: 10, pb: 1}}>
                {
                    alerts &&
                        <Alert
                            variant='filled'
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <>
                                    <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                        {t('custom_reports.reports.table.dialog.edit.alert.unsaved_changes.buttons.yes')}
                                    </Button>
                                    <Button onClick={() => setAlerts(false)} color='inherit' size='small' variant='text' disableElevation>
                                        {t('custom_reports.reports.table.dialog.edit.alert.unsaved_changes.buttons.no')}
                                    </Button>
                                </>
                            }
                        >
                            <AlertTitle>{t('custom_reports.reports.table.dialog.edit.alert.unsaved_changes.message')}</AlertTitle>
                        </Alert>
                }
            </Box>
        <DialogContent dividers>
            <Grid container>
                <Grid size={{ xs: 10, sm: 12, md: 12, lg: 12 }}>
                    <TextField
                        id={'input-name'}
                        fullWidth
                        label={t('custom_reports.reports.table.dialog.edit.name')}
                        value={name}
                        autoComplete="off"
                        onChange={(e) => {
                            setName(e.target.value)
                            setNameLength(e.target.value.length)
                            existName(e.target.value)
                        }}
                        placeholder={t('custom_reports.reports.table.dialog.edit.max_name')}
                        required
                        error={existName(name)}
                        slotProps={{
                            htmlInput: { maxLength: 100 },
                            input: {
                                endAdornment: <InputAdornment position="end">{`${nameLength}/100`}</InputAdornment>,
                            },
                        }}
                    />
                    <FormHelperText
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.5,
                            px: 1.75
                        }}
                    >
                        <span>{existName(name) ? t('custom_reports.reports.table.dialog.edit.exist_name') : ''}</span>
                    </FormHelperText>
                </Grid>
                <Grid size={{ xs: 10, sm: 12, md: 12, lg: 12 }} sx={{ mt: 1.5}}>
                    <FormControl fullWidth>
                        <InputLabel id="select-label-status">{t('custom_reports.reports.table.dialog.edit.status')}</InputLabel>
                        <Select
                            labelId="select-label-status"
                            id="select-label-status"
                            value={status}
                            label={t('custom_reports.reports.table.dialog.edit.status')}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            <MenuItem value={"Enabled"}>{t('custom_reports.reports.table.dialog.edit.select_status.enabled')}</MenuItem>
                            <MenuItem value={"Disabled"}>{t('custom_reports.reports.table.dialog.edit.select_status.disabled')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 10, sm: 12, md: 12, lg: 12 }} sx={{ mt: 1.5}}>
                    <FormControl fullWidth>
                        <InputLabel id="select-label-foremployee">{t('custom_reports.reports.table.dialog.edit.for_employee')}</InputLabel>
                        <Select
                            labelId="select-label-foremployee"
                            id="select-label-foremployee"
                            value={for_employee}
                            label={t('custom_reports.reports.table.dialog.edit.for_employee')}
                            onChange={(e) => setForEmployee(e.target.value)}
                        >
                            <MenuItem value={"Yes"}>{t('custom_reports.reports.table.dialog.edit.select_for_employee.yes')}</MenuItem>
                            <MenuItem value={"No"}>{t('custom_reports.reports.table.dialog.edit.select_for_employee.no')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid size={{ xs: 10, sm: 12, md: 12, lg: 12 }} sx={{ mt: 1.5}}>
                <TextField
                    id="input-slug"
                    fullWidth
                    label={t('custom_reports.reports.table.dialog.edit.slug')}
                    value={slug}
                    autoComplete="off"
                    onChange={(e) => {
                        setSlug(e.target.value)
                        setSlugLength(e.target.value.length)
                    }}
                    placeholder={t('custom_reports.reports.table.dialog.edit.max_slug')}
                    required
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${slugLength}/100`}</InputAdornment>,
                        }
                    }}
                />
            </Grid>
            <Grid size={{ xs: 10, sm: 12, md: 12, lg: 12 }} sx={{ mt: 1.5}}>
                <FormControl fullWidth>
                    <InputLabel id="select-label-category">{t('custom_reports.reports.table.dialog.edit.category')}</InputLabel>
                    <Select
                        labelId="select-label-category"
                        id="select-label-category"
                        value={category}
                        label={t('custom_reports.reports.table.dialog.edit.category')}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <MenuItem value={1}>Category Default</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 10, sm: 12, md: 12, lg: 12 }} sx={{ mt: 1.5}}>
                <TextField
                    id="input-description"
                    fullWidth
                    label={t('custom_reports.reports.table.dialog.edit.description')}
                    value={description}
                    autoComplete="off"
                    onChange={(e) => {
                        setDescription(e.target.value)
                        setDescriptionLength(e.target.value.length)
                    }}
                    rows={4}
                    multiline
                    placeholder={t('custom_reports.reports.table.dialog.edit.max_description')}
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${descriptionLength}/255`}</InputAdornment>,
                        }
                    }}
                />
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button variant="contained" color="error" onClick={() => changes()}>{t('custom_reports.reports.table.dialog.edit.buttons.cancel')}</Button>
            <Button variant="contained" color="primary" loading={loading} disabled={disabled()} onClick={() => editReport()}>{t('custom_reports.reports.table.dialog.edit.buttons.edit')}</Button>
        </DialogActions>
        </Dialog>
    )
}

export { EditReports };